import React, { useEffect, useState } from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import PhaseHeader from "@/components/PhaseHeader";
import PhaseFooter from "@/components/PhaseFooter";
import LabeledField from "@/components/LabeledField";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Truck, Ruler, Building2, Layers, Settings2, DollarSign } from "lucide-react";
import DoorDiagramSvg from "@/components/brand/DoorDiagramSvg";
import ToggleField from "@/components/ToggleField";
import { listProfilePresets } from "@/lib/api";
import { Link } from "react-router-dom";

export default function Phase1TrailerConfig() {
  const { state, updateClient, updateTrailer, updateCustomer, updatePrimo, next } = useSimulation();
  const { t } = useI18n();
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);

  useEffect(() => {
    setPresetsLoading(true);
    listProfilePresets()
      .then((rows) => setPresets(rows || []))
      .catch(() => setPresets([]))
      .finally(() => setPresetsLoading(false));
  }, []);

  const applyPreset = (id) => {
    if (!id) {
      updateTrailer({ profile_preset_id: null });
      return;
    }
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    // Apply preset — includes door dimensions + costs.
    // Labour hourly rate is NOT part of the profile; it stays independent.
    updateTrailer({
      profile_preset_id: id,
      profile_thickness_mm: p.profile_thickness_mm ?? 85,
      height_mm: p.height_mm ?? 2600,
      width_mm: p.width_mm ?? 1200,
      right_height_mm: p.height_mm ?? 2600,
      right_width_mm: p.width_mm ?? 1200,
    });
    updateCustomer(p.customer || {});
    updatePrimo(p.primo || {});
  };

  return (
    <div className="wizard-phase phase-in max-w-7xl mx-auto px-4 sm:px-6">
      <PhaseHeader
        eyebrow={t("phases.p1")}
        title={t("trailer.title")}
        subtitle={t("trailer.subtitle")}
      />

      <div className="wizard-body">
        <div className="wizard-body-scroll grid grid-cols-1 gap-3">
          {/* Form column */}
          <div className="space-y-3 min-w-0">
            {/* Client info */}
            <section className="card-tech p-3 lg:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h2 className="font-display text-sm font-semibold text-[color:var(--text)]">{t("client.title")}</h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <LabeledField
                  id="client-company"
                  label={t("client.company")}
                  type="text"
                  value={state.client.company_name}
                  onChange={(v) => updateClient({ company_name: v })}
                  placeholder={t("client.companyPh")}
                  testId="client-company-input"
                />
                <LabeledField
                  id="client-contact"
                  label={t("client.contact")}
                  type="text"
                  value={state.client.contact_name}
                  onChange={(v) => updateClient({ contact_name: v })}
                  placeholder={t("client.contactPh")}
                  testId="client-contact-input"
                />
                <LabeledField
                  id="client-email"
                  label={t("client.email")}
                  type="email"
                  value={state.client.contact_email}
                  onChange={(v) => updateClient({ contact_email: v })}
                  placeholder={t("client.emailPh")}
                  testId="client-email-input"
                />
                <div className="flex flex-col gap-1">
                  <Label
                    htmlFor="client-notes"
                    className="text-xs font-semibold text-[color:var(--text)] font-body"
                  >
                    {t("client.notes")}
                  </Label>
                  <Textarea
                    id="client-notes"
                    value={state.client.notes}
                    onChange={(e) => updateClient({ notes: e.target.value })}
                    placeholder={t("client.notesPh")}
                    data-testid="client-notes-input"
                    className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-body h-9 min-h-0 rounded focus-visible:ring-2 focus-visible:ring-[color:var(--brand)] text-sm px-2.5 py-1"
                  />
                </div>
              </div>
            </section>

            {/* Profile preset — FIRST, before trailer characteristics */}
            <section className="card-tech p-3 lg:p-4" data-testid="preset-section">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                  <h2 className="font-display text-sm font-semibold text-[color:var(--text)]">
                    {t("trailer.profileSection")}
                  </h2>
                </div>
                <Link
                  to="/admin/presets"
                  data-testid="manage-presets-link"
                  className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--brand)] hover:underline flex items-center gap-1"
                >
                  <Settings2 className="w-3 h-3" />
                  {t("trailer.managePresets")}
                </Link>
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="preset-select" className="text-xs font-semibold text-[color:var(--text)] font-body">
                  {t("trailer.profilePreset")}
                </Label>
                <select
                  id="preset-select"
                  data-testid="preset-select"
                  value={state.trailer.profile_preset_id || ""}
                  onChange={(e) => applyPreset(e.target.value || null)}
                  disabled={presetsLoading}
                  className="bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text)] font-display font-semibold text-sm h-9 px-2.5 rounded focus-visible:ring-2 focus-visible:ring-[color:var(--brand)] focus-visible:border-[color:var(--brand)] transition-colors"
                >
                  <option value="">— {t("trailer.profileChoose")} —</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.is_system ? "" : "  •  custom"}
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-[color:var(--text-mute)] font-mono">
                  {t("trailer.profileHint")}
                </div>
              </div>
            </section>

            {/* Trailer characteristics */}
            <section className="card-tech p-3 lg:p-4">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h2 className="font-display text-sm font-semibold text-[color:var(--text)]">{t("trailer.title")}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <LabeledField
                  id="trailer-height"
                  label={t("trailer.height")}
                  value={state.trailer.height_mm}
                  onChange={(v) => updateTrailer({ height_mm: v, right_height_mm: v })}
                  unit="mm"
                  step="1"
                  testId="trailer-height-input"
                />
                <LabeledField
                  id="trailer-width"
                  label={t("trailer.width")}
                  value={state.trailer.width_mm}
                  onChange={(v) => updateTrailer({ width_mm: v, right_width_mm: v })}
                  unit="mm"
                  step="1"
                  testId="trailer-width-input"
                />

                <LabeledField
                  id="trailer-doors"
                  label={t("trailer.doors")}
                  value={state.trailer.doors_per_trailer}
                  onChange={(v) => updateTrailer({ doors_per_trailer: v })}
                  step="1"
                  testId="trailer-doors-input"
                />

                <div className="md:col-span-2 flex flex-col gap-1">
                  <Label className="text-xs font-semibold text-[color:var(--text)] font-body">
                    {t("trailer.period")}
                  </Label>
                  <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={t("trailer.period")}>
                    {[
                      { code: "weekly", label: t("trailer.weekly") },
                      { code: "monthly", label: t("trailer.monthly") },
                      { code: "annual", label: t("trailer.annualPeriod") },
                    ].map((p) => {
                      const selected = state.trailer.production_period === p.code;
                      return (
                        <button
                          key={p.code}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          data-testid={`period-${p.code}`}
                          onClick={() => updateTrailer({ production_period: p.code })}
                          className={[
                            "h-9 px-2 rounded font-mono text-xs tracking-wide border transition-colors",
                            selected
                              ? "bg-[color:var(--navy)] border-[color:var(--navy)] text-white"
                              : "bg-[color:var(--surface-2)] border-[color:var(--border)] text-[color:var(--text)] hover:border-[color:var(--navy)]",
                          ].join(" ")}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <LabeledField
                    id="trailer-annual"
                    label={t("trailer.annual")}
                    value={state.trailer.annual_production}
                    onChange={(v) => updateTrailer({ annual_production: v })}
                    step="1"
                    testId="trailer-annual-input"
                    hint={t("trailer.quantityHint", {
                      period: t(
                        state.trailer.production_period === "weekly"
                          ? "trailer.weekly"
                          : state.trailer.production_period === "monthly"
                            ? "trailer.monthly"
                            : "trailer.annualPeriod"
                      ).toLowerCase(),
                    })}
                  />
                </div>
              </div>
            </section>

            {/* Labour hourly rate — independent from profile preset */}
            <section className="card-tech p-3 lg:p-4" data-testid="hourly-rate-section">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h2 className="font-display text-sm font-semibold text-[color:var(--text)]">
                  {t("trailer.hourlyRateSection")}
                </h2>
              </div>
              <div className="max-w-sm">
                <LabeledField
                  id="trailer-hourly"
                  label={t("trailer.hourlyRate")}
                  value={state.trailer.labor_hourly_rate}
                  onChange={(v) => updateTrailer({ labor_hourly_rate: v })}
                  unit="€/h"
                  step="0.01"
                  testId="trailer-hourly-input"
                  hint={t("trailer.hourlyRateHint")}
                />
              </div>
            </section>
          </div>

          {/* RIGHT: Visual — removed per client request */}
        </div>
      </div>

      <PhaseFooter onNext={next} />
    </div>
  );
}
